(function (root, factory) {
  const CfgDoorKit = factory();
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { CfgDoorKit };
  } else {
    root.CfgDoorKit = CfgDoorKit;
  }
})(typeof window !== "undefined" ? window : globalThis, function () {
  const EPS = 1e-6;
  const PENTURE_GLASS_180 = "Verre à verre 180°";
  const PENTURE_WALL = "Fixé au mur ou toute surface droite";
  const LOQUET_WALL = "Fixé au mur ou toute surface droite";
  const LOQUET_180_HOUSE = "Verre à verre 180° ouverture vers maison";
  const LOQUET_180_POOL = "Verre à verre 180° ouverture vers piscine";
  const LOQUET_90_BAS = "Verre à verre 90° ouverture vers maison (bas)";
  const LOQUET_90_HAUT = "Verre à verre 90° ouverture vers maison (haut)";

  function isZeroEff(n) {
    return !Number.isFinite(n) || n <= EPS;
  }

  function snapHingeEffective({ previousEff, nextEff, doorWidth }) {
    if (!Number.isFinite(doorWidth) || doorWidth <= 0) return nextEff;
    if (!Number.isFinite(nextEff)) return nextEff;
    if (nextEff <= EPS) return 0;
    if (nextEff + EPS >= doorWidth) return nextEff;
    return isZeroEff(previousEff) ? doorWidth : 0;
  }

  function resolveHingeEffective({ hingeEff, doorWidth, maxEff }) {
    if (!Number.isFinite(doorWidth) || doorWidth <= 0) return hingeEff;
    if (isZeroEff(hingeEff)) return 0;
    const max = Number.isFinite(maxEff) ? maxEff : Infinity;
    if (hingeEff + EPS >= doorWidth) return Math.min(hingeEff, max);
    if (doorWidth <= max + EPS) return doorWidth;
    return 0;
  }

  function normalizeDetail(value) {
    const v = String(value || "")
      .toLowerCase()
      .replace(/\s*\+\s*/g, "_")
      .replace(/\s+/g, "_");
    if (
      v.includes("loquet_verrou_plancher") ||
      v.includes("locket_verrou_plancher") ||
      v.includes("latch_floor_lock") ||
      v.includes("latch+floor_lock") ||
      v.includes("floor_lock+latch")
    ) {
      return "locket_verrou_plancher";
    }
    if (v.includes("verrou_plancher") || v.includes("floor_lock")) return "verrou_plancher";
    if (v.includes("loquet") || v.includes("locket") || v.includes("latch")) return "locket";
    return v || "locket";
  }

  function parseInchOption(value) {
    const m = String(value || "")
      .replace(",", ".")
      .match(/(\d+(?:\.\d+)?)/);
    return m ? Number(m[1]) : NaN;
  }

  function heightInchesFromCode(heightCode) {
    if (heightCode === "H34") return 34;
    if (heightCode === "H40") return 40;
    if (heightCode === "H46") return 46;
    return NaN;
  }

  function optionMap(variant) {
    const out = {};
    const opts = variant && variant.options;
    if (opts && typeof opts === "object" && !Array.isArray(opts)) {
      Object.keys(opts).forEach((key) => {
        out[String(key).toLowerCase()] = opts[key];
      });
    }
    return out;
  }

  function namedOption(map, names) {
    for (let i = 0; i < names.length; i++) {
      const v = map[names[i]];
      if (v != null && String(v).trim() !== "") return v;
    }
    return "";
  }

  function blob(v) {
    return [
      v && v.product_handle,
      v && v.handle,
      v && v.title,
      v && v.metafields && v.metafields.type,
    ]
      .map((s) => String(s || "").toLowerCase())
      .join(" ");
  }

  function isSpringKit(v) {
    const b = blob(v);
    if (b.includes("polaris")) return false;
    return b.includes("ressort") || b.includes("spring");
  }

  function matchesFinish(v, wantNoir) {
    const b = blob(v);
    const isNoir = b.includes("noir") || b.includes("black");
    const isInox = b.includes("inox") || b.includes("stainless");
    if (wantNoir) return isNoir;
    if (isInox) return true;
    if (isNoir) return false;
    return true;
  }

  function metafieldMatch(v, heightCode, lengthIn, detailKey) {
    const mf = (v && v.metafields) || {};
    const mfLength = Number(mf.length_in);
    return (
      normalizeDetail(mf.detail) === detailKey &&
      String(mf.height_code || "") === String(heightCode || "") &&
      Number.isFinite(mfLength) &&
      Math.round(mfLength) === Math.round(lengthIn)
    );
  }

  function optionMatch(v, heightCode, lengthIn, detailKey) {
    const map = optionMap(v);
    const heightIn = heightInchesFromCode(heightCode);
    const h = parseInchOption(namedOption(map, ["hauteur", "height"]) || v.option1);
    const w = parseInchOption(namedOption(map, ["largeur", "width", "length"]) || v.option2);
    const d = normalizeDetail(namedOption(map, ["verrouillage", "locking"]) || v.option3);
    return (
      Number.isFinite(h) &&
      Number.isFinite(w) &&
      Math.round(h) === Math.round(heightIn) &&
      Math.round(w) === Math.round(lengthIn) &&
      d === detailKey
    );
  }

  function matchDoorKit({ kits, finish, heightCode, lengthIn, detail }) {
    const list = Array.isArray(kits) ? kits : [];
    const finishRaw = String(finish || "").toLowerCase();
    const wantNoir = finishRaw === "matte_black" || finishRaw === "noir";
    const detailKey = normalizeDetail(detail);
    const length = Number(lengthIn);
    if (!heightCode || !Number.isFinite(length)) return null;
    const ressort = list.filter(isSpringKit).filter((v) => matchesFinish(v, wantNoir));
    const byMf = ressort.find((v) => metafieldMatch(v, heightCode, length, detailKey));
    if (byMf) return byMf;
    return ressort.find((v) => optionMatch(v, heightCode, length, detailKey)) || null;
  }

  function buildDoorKitLineItemProperties({
    door,
    cornerFlags,
    hingeOnA1,
    hingeSegHasLen,
    latchSegHasLen,
    hasLoquet,
  }) {
    const hingeAtCorner = hingeOnA1 ? !!cornerFlags?.cornerAtStart : !!cornerFlags?.cornerAtEnd;
    const latchOnA1 = !hingeOnA1;
    const latchAtCorner = latchOnA1 ? !!cornerFlags?.cornerAtStart : !!cornerFlags?.cornerAtEnd;
    const opensTowardPool = String((door && door.swing) || "").toLowerCase() === "outside";

    const penture = hingeSegHasLen || hingeAtCorner ? PENTURE_GLASS_180 : PENTURE_WALL;
    const properties = { Penture: penture };
    if (!hasLoquet) return properties;

    let loquet = LOQUET_WALL;
    if (latchSegHasLen) {
      loquet = opensTowardPool ? LOQUET_180_POOL : LOQUET_180_HOUSE;
    } else if (latchAtCorner) {
      loquet = latchOnA1 ? LOQUET_90_BAS : LOQUET_90_HAUT;
    }
    properties.Loquet = loquet;
    return properties;
  }

  return {
    EPS: EPS,
    snapHingeEffective: snapHingeEffective,
    resolveHingeEffective: resolveHingeEffective,
    matchDoorKit: matchDoorKit,
    buildDoorKitLineItemProperties: buildDoorKitLineItemProperties,
    normalizeDetail: normalizeDetail,
  };
});
